import axios from 'axios';

// Strapi v5 API configuration
const STRAPI_API_BASE_URL = 'https://api.locrefid.coal.mx/api';

// Create axios instance with default config
const apiClient = axios?.create({
  baseURL: STRAPI_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Vehicle API service
export const vehicleApi = {
  /**
   * Fetch vehicles from Strapi v5 API
   * @param {Object} options - Query options
   * @param {number} options.page - Page number for pagination
   * @param {number} options.pageSize - Number of items per page
   * @param {boolean} options.flagFilter - Filter by flag status
   * @param {string} options.sortBy - Sort field and direction (e.g., 'id:asc')
   * @returns {Promise<Object>} API response with vehicles data and pagination meta
   */
  async fetchVehicles(options = {}) {
    const {
      page = 1,
      pageSize = 25,
      flagFilter = true,
      sortBy = 'id:asc'
    } = options;

    try {
      const params = new URLSearchParams();
      
      // Add filters - Strapi v5 format
      if (flagFilter !== undefined) {
        params?.append('filters[flag][$eq]', flagFilter?.toString());
      }
      
      // Add sorting
      params?.append('sort[0]', sortBy);
      
      // Add pagination
      params?.append('pagination[page]', page?.toString());
      params?.append('pagination[pageSize]', pageSize?.toString());

      const response = await apiClient?.get(`/vehicles?${params?.toString()}`);
      
      return {
        success: true,
        data: response?.data?.data || [],
        meta: response?.data?.meta || {},
        rawResponse: response?.data
      };
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      
      return {
        success: false,
        data: [],
        meta: {},
        error: {
          message: error?.response?.data?.error?.message || error?.message || 'Error desconocido',
          status: error?.response?.status || 500,
          details: error?.response?.data || null
        }
      };
    }
  },

  /**
   * Fetch a single vehicle by ID or documentId
   * @param {string} id - Vehicle ID or documentId
   * @returns {Promise<Object>} API response with vehicle data
   */
  async fetchVehicleById(id) {
    try {
      const response = await apiClient?.get(`/vehicles/${id}`);
      
      return {
        success: true,
        data: response?.data?.data || null,
        meta: response?.data?.meta || {}
      };
    } catch (error) {
      console.error('Error fetching vehicle by ID:', error);
      
      return {
        success: false,
        data: null,
        error: {
          message: error?.response?.data?.error?.message || error?.message || 'Error desconocido',
          status: error?.response?.status || 500,
          details: error?.response?.data || null
        }
      };
    }
  },

  /**
   * Search vehicles by plate number
   * @param {string} plate - Plate number to search
   * @returns {Promise<Object>} API response with matching vehicles
   */
  async searchVehiclesByPlate(plate) {
    try {
      const params = new URLSearchParams();
      params?.append('filters[plate][$containsi]', plate);
      params?.append('sort[0]', 'updatedAt:desc');
      
      const response = await apiClient?.get(`/vehicles?${params?.toString()}`);
      
      return {
        success: true,
        data: response?.data?.data || [],
        meta: response?.data?.meta || {}
      };
    } catch (error) {
      console.error('Error searching vehicles by plate:', error);
      
      return {
        success: false,
        data: [],
        error: {
          message: error?.response?.data?.error?.message || error?.message || 'Error desconocido',
          status: error?.response?.status || 500
        }
      };
    }
  },

  /**
   * Update vehicle flag status
   * @param {string} documentId - Vehicle documentId from Strapi
   * @param {boolean} flagValue - Flag value to set (default: false to mark as read)
   * @returns {Promise<Object>} API response
   */
  async updateVehicleFlag(documentId, flagValue = false) {
    try {
      const response = await apiClient?.put(`/vehicles/${documentId}`, {
        data: {
          flag: flagValue
        }
      });
      
      return {
        success: true,
        data: response?.data?.data || null,
        meta: response?.data?.meta || {}
      };
    } catch (error) {
      console.error('Error updating vehicle flag:', error);
      
      return {
        success: false,
        data: null,
        error: {
          message: error?.response?.data?.error?.message || error?.message || 'Error al actualizar vehículo',
          status: error?.response?.status || 500,
          details: error?.response?.data || null
        }
      };
    }
  },

  /**
   * Mark multiple vehicles as read (batch update)
   * @param {Array} documentIds - Array of documentIds to mark as read
   * @returns {Promise<Object>} Results of batch update
   */
  async markVehiclesAsRead(documentIds) {
    if (!Array.isArray(documentIds) || documentIds?.length === 0) {
      return {
        success: false,
        error: { message: 'No se proporcionaron IDs de vehículos válidos' }
      };
    }

    const results = {
      success: true,
      updated: [],
      failed: []
    };

    // Process each vehicle update
    for (const documentId of documentIds) {
      try {
        const updateResult = await this.updateVehicleFlag(documentId, false);
        
        if (updateResult?.success) {
          results?.updated?.push(documentId);
        } else {
          results?.failed?.push({
            documentId,
            error: updateResult?.error?.message || 'Error desconocido'
          });
        }
      } catch (error) {
        results?.failed?.push({
          documentId,
          error: error?.message || 'Error de conexión'
        });
      }
    }

    // Set overall success based on results
    if (results?.failed?.length > 0 && results?.updated?.length === 0) {
      results.success = false;
    }

    return results;
  }
};

// Transform Strapi v5 vehicle data to internal format
export const transformVehicleData = (strapiVehicle) => {
  if (!strapiVehicle) return null;

  return {
    id: strapiVehicle?.id?.toString() || '',
    documentId: strapiVehicle?.documentId || '',
    serialNumber: strapiVehicle?.serialNumber || 'N/A',
    plate: strapiVehicle?.plate || 'N/A',
    make: strapiVehicle?.make || 'Desconocido',
    model: strapiVehicle?.model || 'Desconocido',
    year: strapiVehicle?.year || null,
    flag: strapiVehicle?.flag || false,
    detectedAt: strapiVehicle?.updatedAt || strapiVehicle?.createdAt || new Date()?.toISOString(),
    createdAt: strapiVehicle?.createdAt || null,
    updatedAt: strapiVehicle?.updatedAt || null,
    publishedAt: strapiVehicle?.publishedAt || null
  };
};

// Transform multiple vehicles
export const transformVehiclesData = (strapiVehicles) => {
  if (!Array.isArray(strapiVehicles)) return [];
  return strapiVehicles?.map(transformVehicleData)?.filter(Boolean);
};

export default vehicleApi;